import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "./Stock.css";
import { ToastContainer, toast } from "react-toastify";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { UilAngleLeft, UilAngleRight, UilCheck, UilTrashAlt, UilPlus } from "@iconscout/react-unicons";
import { userRequest } from "../../RequestMethods";
import { markAlertsSeen } from "../../utils/lowStockAlertsSeen";
import { getThresholdError } from "../../utils/thresholdValidation";

const MOVEMENT_TYPES = ["", "in", "out", "reserve", "release", "return", "correction"];
const ADJUST_TYPES = ["in", "out", "correction", "return"];

const emptyNewLine = { color: "", size: "", minThreshold: "0", initialQuantity: "0" };
const emptyAdjustDraft = { quantity: "", type: "correction", reason: "" };

const Stock = () => {
  const [searchParams] = useSearchParams();

  // ── Product picker + inventory browse/CRUD ──────────────────────────
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [inventoryItems, setInventoryItems] = useState([]);
  const [packsAvailableByColor, setPacksAvailableByColor] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState(null);
  const [thresholdDrafts, setThresholdDrafts] = useState({});
  const [adjustDrafts, setAdjustDrafts] = useState({});
  const [flashSuccessKeys, setFlashSuccessKeys] = useState({});
  const [applyingKeys, setApplyingKeys] = useState({});
  const [newLine, setNewLine] = useState(emptyNewLine);
  const [newLineSubmitting, setNewLineSubmitting] = useState(false);

  // ── Low stock alerts - scoped to the selected product ────────────────
  const [alerts, setAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [alertsError, setAlertsError] = useState(null);

  // ── Movement history - scoped to the selected product ───────────────
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  const [movementsPagination, setMovementsPagination] = useState({ page: 1, limit: 25, total: 0, pages: 1 });
  const [movementTypeFilter, setMovementTypeFilter] = useState("");

  const fetchProducts = async () => {
    try {
      // Cache-bust: this exact URL was served with a long-lived cache header
      // until a recent fix, so browsers that already cached it from before
      // that fix won't re-check the server on their own for up to an hour.
      // The timestamp param guarantees every call is treated as a new
      // resource regardless of what's still sitting in the HTTP cache.
      const res = await userRequest.get("products/dropdown", { params: { _: Date.now() } });
      setProducts(res.data.products || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des produits.");
    }
  };

  // silent skips the loading-spinner swap - used after an inline edit
  // (threshold save, quick adjust) so the table just quietly refreshes its
  // numbers instead of the whole section flashing to a spinner and back.
  const fetchInventory = async (productId, { silent = false } = {}) => {
    if (!productId) {
      setInventoryItems([]);
      setPacksAvailableByColor({});
      return;
    }
    try {
      if (!silent) setInventoryLoading(true);
      const res = await userRequest.get(`stock/product/${productId}`);
      setInventoryItems(res.data.items || []);
      setPacksAvailableByColor(res.data.packsAvailableByColor || {});
      setInventoryError(null);
    } catch (err) {
      setInventoryError(err?.response?.data?.message || "Erreur lors du chargement du stock de ce produit.");
    } finally {
      if (!silent) setInventoryLoading(false);
    }
  };

  // Briefly turns a button green to confirm an inline edit landed, on top
  // of the toast - key is scoped per action+item so unrelated rows/buttons
  // never flash together.
  const flashSuccess = (key) => {
    setFlashSuccessKeys((prev) => ({ ...prev, [key]: true }));
    setTimeout(() => {
      setFlashSuccessKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }, 2000);
  };

  const fetchAlerts = async (productId) => {
    if (!productId) {
      setAlerts([]);
      return;
    }
    try {
      setAlertsLoading(true);
      const res = await userRequest.get("stock/alerts", { params: { productId } });
      const fetchedAlerts = res.data.alerts || [];
      setAlerts(fetchedAlerts);
      setAlertsError(null);
      markAlertsSeen(fetchedAlerts.map((alert) => alert._id));
    } catch (err) {
      setAlertsError(err?.response?.data?.message || "Erreur lors du chargement des alertes de stock.");
    } finally {
      setAlertsLoading(false);
    }
  };

  const fetchMovements = async (productId, page = 1, { silent = false } = {}) => {
    if (!productId) {
      setMovements([]);
      setMovementsPagination({ page: 1, limit: 25, total: 0, pages: 1 });
      return;
    }
    try {
      if (!silent) setMovementsLoading(true);
      const params = { page, limit: movementsPagination.limit, productId };
      if (movementTypeFilter) params.type = movementTypeFilter;

      const res = await userRequest.get("stock/movements", { params });
      setMovements(res.data.movements || []);
      setMovementsPagination(res.data.pagination || { page: 1, limit: 25, total: 0, pages: 1 });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Erreur lors du chargement des mouvements.");
    } finally {
      if (!silent) setMovementsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();

    const productIdFromUrl = searchParams.get("productId");
    if (productIdFromUrl) {
      setSelectedProductId(productIdFromUrl);
      fetchInventory(productIdFromUrl);
      fetchAlerts(productIdFromUrl);
      fetchMovements(productIdFromUrl, 1);
    }
    // Depends on the productId query param, not just mount - a redirect here
    // right after creating a product (e.g. from AddProduct.js) is a client-
    // side navigation, not a full reload, so without this the dropdown would
    // keep showing the stale product list fetched before that product existed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get("productId")]);

  // Also refetch the product list whenever this tab regains focus/visibility
  // - covers a product being created/deleted in a *different* tab while this
  // Stock tab just sat open (no URL change here to key off of).
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") fetchProducts();
    };
    window.addEventListener("focus", handleVisibility);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("focus", handleVisibility);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  // Belt-and-suspenders: focus/visibility events only fire on an actual
  // OS-level tab switch. Same-tab in-app navigation back to an already-
  // mounted Stock page, or simply leaving this tab open the whole time,
  // triggers neither - so poll too, same pattern as the sidebar badges.
  useEffect(() => {
    const interval = setInterval(fetchProducts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSelectProduct = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    setThresholdDrafts({});
    setAdjustDrafts({});
    setNewLine(emptyNewLine);
    fetchInventory(productId);
    fetchAlerts(productId);
    fetchMovements(productId, 1);
  };

  const handleThresholdChange = (itemId, value) => {
    setThresholdDrafts({ ...thresholdDrafts, [itemId]: value });
  };

  const handleSaveThreshold = async (item) => {
    const value = thresholdDrafts[item._id];
    if (value === undefined || value === "") return;
    const key = `threshold-${item._id}`;
    if (applyingKeys[key]) return;
    const validationError = getThresholdError(value, item.availableQty);
    if (validationError) {
      toast.error(validationError);
      return;
    }
    // Locked the instant the button is clicked, before the request starts,
    // so a second click can't send the same value twice.
    setApplyingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      await userRequest.patch(`stock/inventory/${item._id}`, { minThreshold: Number(value) });
      toast.success("Seuil mis à jour.");
      flashSuccess(key);
      // Wait for the refreshed value, then drop the draft: the button stays
      // locked until the threshold is edited again.
      await fetchInventory(selectedProductId, { silent: true });
      setThresholdDrafts((prev) => {
        const next = { ...prev };
        delete next[item._id];
        return next;
      });
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Erreur lors de la mise à jour du seuil.");
    } finally {
      setApplyingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleAdjustDraftChange = (itemId, field, value) => {
    setAdjustDrafts({
      ...adjustDrafts,
      [itemId]: { ...(adjustDrafts[itemId] || emptyAdjustDraft), [field]: value },
    });
  };

  const handleApplyAdjust = async (item) => {
    const draft = adjustDrafts[item._id] || emptyAdjustDraft;
    if (!draft.quantity) {
      toast.error("Quantité requise pour ajuster.");
      return;
    }
    const key = `adjust-${item._id}`;
    // Turns green the instant you click, before the request even starts -
    // no gap where the button still looks untouched while waiting on the
    // network. flashSuccess then takes over once it actually succeeds.
    setApplyingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      await userRequest.post("stock/adjust", {
        productId: item.productId,
        color: item.color,
        size: item.size,
        quantity: Number(draft.quantity),
        type: draft.type,
        reason: draft.reason,
      });
      toast.success("Stock ajusté avec succès.");
      flashSuccess(key);
      setAdjustDrafts({ ...adjustDrafts, [item._id]: emptyAdjustDraft });
      // Wait for the refreshed number before flashing its cell, so the
      // highlight lands on the actual new value, not the stale one -
      // otherwise it's easy to miss which column just changed.
      await fetchInventory(selectedProductId, { silent: true });
      flashSuccess(`qty-${item._id}`);
      fetchMovements(selectedProductId, 1, { silent: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors de l'ajustement du stock.");
    } finally {
      setApplyingKeys((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleDeleteLine = async (item) => {
    if (!window.confirm(`Supprimer la ligne de stock ${item.color || "—"} / ${item.size} ?`)) return;
    try {
      await userRequest.delete(`stock/inventory/${item._id}`);
      toast.success("Ligne de stock supprimée.");
      fetchInventory(selectedProductId);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Erreur lors de la suppression.");
    }
  };

  const handleNewLineChange = (e) => {
    setNewLine({ ...newLine, [e.target.name]: e.target.value });
  };

  const handleCreateLine = async (e) => {
    e.preventDefault();
    if (!selectedProductId || !newLine.size) {
      toast.error("Sélectionnez un produit et renseignez une taille.");
      return;
    }
    const threshold = Number(newLine.minThreshold) || 0;
    const quantity = Number(newLine.initialQuantity) || 0;
    if (threshold < 0) {
      toast.error("Le seuil ne peut pas être négatif.");
      return;
    }
    if (threshold > 0 && threshold >= quantity) {
      toast.error(`Le seuil doit être inférieur à la quantité initiale (${quantity}).`);
      return;
    }
    try {
      setNewLineSubmitting(true);
      await userRequest.post("stock/inventory", {
        productId: selectedProductId,
        color: newLine.color,
        size: newLine.size,
        minThreshold: threshold,
        initialQuantity: quantity,
      });
      toast.success("Ligne de stock créée.");
      setNewLine(emptyNewLine);
      fetchInventory(selectedProductId);
    } catch (err) {
      const data = err?.response?.data;
      toast.error(data?.message || data?.error || "Erreur lors de la création de la ligne de stock.");
    } finally {
      setNewLineSubmitting(false);
    }
  };

  const existingColors = [...new Set(inventoryItems.map((item) => item.color).filter(Boolean))];

  const handleAcknowledge = async (alertId) => {
    try {
      await userRequest.post(`stock/alerts/${alertId}/acknowledge`);
      toast.success("Alerte prise en compte.");
      fetchAlerts(selectedProductId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Impossible de prendre en compte l'alerte.");
    }
  };

  const handleMovementTypeFilterChange = (e) => {
    setMovementTypeFilter(e.target.value);
  };

  const handleApplyMovementFilters = (e) => {
    e.preventDefault();
    fetchMovements(selectedProductId, 1);
  };

  const handleMovementsPrevPage = () => {
    if (movementsPagination.page > 1) fetchMovements(selectedProductId, movementsPagination.page - 1);
  };

  const handleMovementsNextPage = () => {
    if (movementsPagination.page < movementsPagination.pages) fetchMovements(selectedProductId, movementsPagination.page + 1);
  };

  return (
    <div className="Stock">
      <h1>Stock</h1>

      <section className="stock-section">
        <h2>Inventaire produit</h2>
        <div className="stock-product-picker">
          <select value={selectedProductId} onChange={handleSelectProduct}>
            <option value="">Sélectionner un produit…</option>
            {products.map((product) => (
              <option key={product._id} value={product._id}>{product.title || product._id}</option>
            ))}
          </select>
        </div>

        {!selectedProductId && <p className="stock-hint">Choisissez un produit pour voir et gérer son stock.</p>}

        {selectedProductId && inventoryLoading && (
          <div className="loading-container">
            <div className="loading-spinner-custom"></div>
          </div>
        )}

        {selectedProductId && !inventoryLoading && inventoryError && (
          <div className="error-message-custom">
            <span className="error-icon">⚠️</span>
            <p>{inventoryError}</p>
            <button className="retry-btn" onClick={() => fetchInventory(selectedProductId)}>Réessayer</button>
          </div>
        )}

        {selectedProductId && !inventoryLoading && !inventoryError && (
          <>
            {Object.keys(packsAvailableByColor).length > 0 && (
              <div className="stock-packs-available">
                {Object.entries(packsAvailableByColor).map(([color, packs]) => (
                  <span key={color} className="badge packs-available">
                    {color === "_default" ? "Packs disponibles" : `Packs disponibles (${color})`}: {packs === null ? "—" : packs}
                  </span>
                ))}
              </div>
            )}

            <TableContainer component={Paper} className="stock-table-container">
              <Table>
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell sx={{ fontWeight: "bold" }}>Couleur</TableCell>
                    <TableCell>Taille</TableCell>
                    <TableCell>Stock disponible (unités à vendre)</TableCell>
                    <TableCell>Réservé (déjà commandé, pas encore expédié)</TableCell>
                    <TableCell>Seuil d'alerte (unités)</TableCell>
                    <TableCell>Ajustement rapide</TableCell>
                    <TableCell>Supprimer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventoryItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">Aucune ligne de stock pour ce produit.</TableCell>
                    </TableRow>
                  )}
                  {inventoryItems.map((item) => {
                    const draft = adjustDrafts[item._id] || emptyAdjustDraft;
                    return (
                      <TableRow key={item._id}>
                        <TableCell>
                          {item.color ? (
                            <div className="stock-color-cell">
                              <span className="color-swatch" style={{ backgroundColor: item.color }}></span>
                              {item.color}
                            </div>
                          ) : "—"}
                        </TableCell>
                        <TableCell>{item.size}</TableCell>
                        <TableCell className={flashSuccessKeys[`qty-${item._id}`] ? "stock-qty-flash" : ""}>
                          {item.availableQty}
                        </TableCell>
                        <TableCell>{item.reservedQty}</TableCell>
                        <TableCell>
                          {(() => {
                            const thresholdDraft = thresholdDrafts[item._id];
                            const thresholdError = getThresholdError(thresholdDraft, item.availableQty);
                            const thresholdKey = `threshold-${item._id}`;
                            const isSavingThreshold = Boolean(applyingKeys[thresholdKey]);
                            const hasChange = thresholdDraft !== undefined && thresholdDraft !== "" && Number(thresholdDraft) !== item.minThreshold;
                            const canSave = hasChange && !thresholdError && !isSavingThreshold;
                            return (
                              <div className="stock-threshold-wrap">
                                <div className="stock-threshold-cell">
                                  <input
                                    type="number"
                                    min={1}
                                    className={thresholdError ? "stock-threshold-input--error" : ""}
                                    value={thresholdDraft ?? item.minThreshold}
                                    onChange={(e) => handleThresholdChange(item._id, e.target.value)}
                                  />
                                  <button
                                    className={`action-icon-btn btn-edit${(isSavingThreshold || flashSuccessKeys[thresholdKey]) ? " flash-success" : ""}`}
                                    onClick={() => handleSaveThreshold(item)}
                                    disabled={!canSave}
                                    title={thresholdError || (hasChange ? "Enregistrer le seuil" : "Modifiez le seuil pour l'enregistrer")}
                                  >
                                    <UilCheck />
                                  </button>
                                </div>
                                <small className="stock-threshold-hint">
                                  {item.availableQty > 1
                                    ? `Stock : ${item.availableQty} · seuil de 1 à ${item.availableQty - 1}`
                                    : `Stock : ${item.availableQty} · aucun seuil possible`}
                                </small>
                                {thresholdError && <small className="stock-threshold-error">{thresholdError}</small>}
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="stock-adjust-cell">
                            <input
                              type="number"
                              placeholder={draft.type === "correction" ? "Nouvelle qté totale" : "Qté"}
                              value={draft.quantity}
                              title={
                                draft.type === "correction"
                                  ? "Quantité totale exacte après recomptage (pas un delta)."
                                  : "Nombre à appliquer - in/return ajoutent, out retire."
                              }
                              onChange={(e) => handleAdjustDraftChange(item._id, "quantity", e.target.value)}
                            />
                            <select
                              value={draft.type}
                              title="in/return ajoutent, out retire, correction fixe la quantité totale (recomptage)"
                              onChange={(e) => handleAdjustDraftChange(item._id, "type", e.target.value)}
                            >
                              {ADJUST_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                            <input
                              type="text" placeholder="Raison" value={draft.reason}
                              onChange={(e) => handleAdjustDraftChange(item._id, "reason", e.target.value)}
                            />
                            <button
                              className={`action-icon-btn btn-primary${(applyingKeys[`adjust-${item._id}`] || flashSuccessKeys[`adjust-${item._id}`]) ? " flash-success" : ""}`}
                              onClick={() => handleApplyAdjust(item)}
                              disabled={applyingKeys[`adjust-${item._id}`]}
                            >
                              {flashSuccessKeys[`adjust-${item._id}`] ? (<><UilCheck /> Appliqué</>) : "Appliquer"}
                            </button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <button className="action-icon-btn btn-delete" onClick={() => handleDeleteLine(item)}>
                            <UilTrashAlt />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <form className="stock-new-line-form stock-new-line-form--labelled" onSubmit={handleCreateLine}>
              <label className="stock-line-field">
                <span>Couleur</span>
                <input
                  type="text" name="color" placeholder="Vide si aucune" list="existing-colors"
                  value={newLine.color} onChange={handleNewLineChange}
                />
              </label>
              <datalist id="existing-colors">
                {existingColors.map((color) => (
                  <option key={color} value={color} />
                ))}
              </datalist>
              <label className="stock-line-field">
                <span>Taille</span>
                <input
                  type="text" name="size" placeholder="ex: M, 8"
                  value={newLine.size} onChange={handleNewLineChange}
                />
              </label>
              <label className="stock-line-field">
                <span>Seuil d'alerte (unités)</span>
                <input
                  type="number" name="minThreshold" placeholder="0 = aucun"
                  value={newLine.minThreshold} onChange={handleNewLineChange}
                />
              </label>
              <label className="stock-line-field">
                <span>Quantité initiale (unités)</span>
                <input
                  type="number" name="initialQuantity" placeholder="0"
                  value={newLine.initialQuantity} onChange={handleNewLineChange}
                />
              </label>
              <button type="submit" className="action-icon-btn btn-primary" disabled={newLineSubmitting}>
                <UilPlus /> {newLineSubmitting ? "…" : "Ajouter une ligne"}
              </button>
            </form>
          </>
        )}
      </section>

      <section className="stock-section">
        <h2>Alertes de stock bas {selectedProductId && "du produit sélectionné"}</h2>

        {!selectedProductId && (
          <p className="stock-hint">
            Sélectionnez un produit ci-dessus pour voir ses alertes de stock bas.
            Pour parcourir tous les produits, voir la page "Alertes de stock bas" dans le menu.
          </p>
        )}

        {selectedProductId && alertsLoading && (
          <div className="loading-container">
            <div className="loading-spinner-custom"></div>
          </div>
        )}

        {selectedProductId && !alertsLoading && alertsError && (
          <div className="error-message-custom">
            <span className="error-icon">⚠️</span>
            <p>{alertsError}</p>
            <button className="retry-btn" onClick={() => fetchAlerts(selectedProductId)}>Réessayer</button>
          </div>
        )}

        {selectedProductId && !alertsLoading && !alertsError && (
          <TableContainer component={Paper} className="stock-table-container">
            <Table>
              <TableHead>
                <TableRow className="table-header">
                  <TableCell sx={{ fontWeight: "bold" }}>Couleur / Taille</TableCell>
                  <TableCell>Quantité au déclenchement</TableCell>
                  <TableCell>Disponible actuel</TableCell>
                  <TableCell>Seuil actuel</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Déclenchée le</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">Aucune alerte active.</TableCell>
                  </TableRow>
                )}
                {alerts.map((alert) => (
                  <TableRow key={alert._id}>
                    <TableCell>
                      {alert.inventoryItemId?.color || "—"} / {alert.inventoryItemId?.size}
                    </TableCell>
                    <TableCell>{alert.currentQty}</TableCell>
                    <TableCell>{alert.inventoryItemId?.availableQty ?? "—"}</TableCell>
                    <TableCell>{alert.inventoryItemId?.minThreshold ?? "—"}</TableCell>
                    <TableCell>
                      <span className={`badge alert-${alert.status}`}>{alert.status}</span>
                    </TableCell>
                    <TableCell>{new Date(alert.triggeredAt).toLocaleString("fr-FR")}</TableCell>
                    <TableCell>
                      {alert.status === "open" && (
                        <button className="action-icon-btn btn-primary" onClick={() => handleAcknowledge(alert._id)}>
                          <UilCheck /> Prendre en compte
                        </button>
                      )}
                      {alert.status === "acknowledged" && (
                        <span className="ack-note">
                          Pris en compte par {alert.acknowledgedBy?.name || "—"}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </section>

      <section className="stock-section">
        <h2>Historique des mouvements {selectedProductId && "du produit sélectionné"}</h2>

        {!selectedProductId && (
          <p className="stock-hint">
            Sélectionnez un produit ci-dessus pour voir son historique de mouvements.
            Pour parcourir tous les produits, voir la page "Historique des mouvements" dans le menu.
          </p>
        )}

        {selectedProductId && (
          <>
            <form className="stock-filters" onSubmit={handleApplyMovementFilters}>
              <select value={movementTypeFilter} onChange={handleMovementTypeFilterChange}>
                <option value="">Tous les types</option>
                {MOVEMENT_TYPES.filter(Boolean).map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <button type="submit" className="action-icon-btn btn-primary">Filtrer</button>
            </form>

            {movementsLoading ? (
              <div className="loading-container">
                <div className="loading-spinner-custom"></div>
              </div>
            ) : (
              <>
                <TableContainer component={Paper} className="stock-table-container">
                  <Table>
                    <TableHead>
                      <TableRow className="table-header">
                        <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
                        <TableCell>Couleur / Taille</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Quantité</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Par</TableCell>
                        <TableCell>Raison</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {movements.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center">Aucun mouvement trouvé.</TableCell>
                        </TableRow>
                      )}
                      {movements.map((movement) => (
                        <TableRow key={movement._id}>
                          <TableCell>{new Date(movement.createdAt).toLocaleString("fr-FR")}</TableCell>
                          <TableCell>
                            <div className="stock-color-cell">
                              {movement.inventoryItemId?.color && (
                                <span
                                  className="color-swatch"
                                  style={{ backgroundColor: movement.inventoryItemId.color }}
                                ></span>
                              )}
                              {movement.inventoryItemId?.size || "—"}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className={`badge movement-${movement.type}`}>{movement.type}</span>
                          </TableCell>
                          <TableCell>{movement.quantity > 0 ? `+${movement.quantity}` : movement.quantity}</TableCell>
                          <TableCell>{movement.sourceType}</TableCell>
                          <TableCell>
                            {movement.performedBy ? `${movement.performedBy.name || ""} ${movement.performedBy.surname || ""}`.trim() : "—"}
                          </TableCell>
                          <TableCell>{movement.reason || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <div className="stock-pagination">
                  <button
                    className="pagination-btn" onClick={handleMovementsPrevPage}
                    disabled={movementsPagination.page <= 1}
                  >
                    <UilAngleLeft /> Précédent
                  </button>
                  <span>
                    Page {movementsPagination.page} / {movementsPagination.pages || 1} ({movementsPagination.total} entrées)
                  </span>
                  <button
                    className="pagination-btn" onClick={handleMovementsNextPage}
                    disabled={movementsPagination.page >= movementsPagination.pages}
                  >
                    Suivant <UilAngleRight />
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </section>

      <ToastContainer position="top-right" />
    </div>
  );
};

export default Stock;
